import { Axios } from 'utils'
import {
  Delivery,
  DeliveryAttributeComparison,
  TSpectralDistanceData,
  INewDelivery,
  ThinDelivery,
  IRecalculateBin,
  IRecalculateBinStatus,
} from 'types/models'
import {
  ItemResponseType,
  ListPaginationParams,
  ListResponseType,
} from './types'
import { IFilterParams } from '../types/models/FilterSort'

export const useDeliveriesService = () => {
  const getDeliveries = async (
    filter_params?: IFilterParams,
    pagination?: ListPaginationParams
  ) => {
    try {
      const response = await Axios.post<ListResponseType<Delivery>>(
        '/deliveries/filters',
        { filter_params: filter_params },
        {
          params: pagination
            ? {
                offset: pagination.currentPage * pagination.itemsPerPage,
                limit: pagination.itemsPerPage,
              }
            : undefined,
        }
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const createDelivery = async (params: Delivery) => {
    try {
      const response = await Axios.post<ItemResponseType<Delivery>>(
        '/deliveries',
        params
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const updateIncomingDeliveryAttribute = async (params: INewDelivery) => {
    try {
      const response = await Axios.put<ItemResponseType<INewDelivery>>(
        `/deliveries/update/attributes/${params.id}`,
        params
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const updateDelivery = async (params: Delivery) => {
    try {
      const response = await Axios.put<ItemResponseType<Delivery>>(
        `/deliveries/${params.id}`,
        params
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getStoredDeliveries = async (wheatCodeId: string | number) => {
    try {
      const response = await Axios.get<ListResponseType<Delivery>>(
        `/deliveries/wheat_code/${wheatCodeId}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getStoredDeliveriesThin = async (
    wheatCodeId: string | number,
    includeDiscarded = false,
    includeStorage = false,
    offset = 0,
    limit = 5
  ) => {
    const response = await Axios.get<ListResponseType<ThinDelivery>>(
      `/deliveries/thin/wheat_code/${wheatCodeId}`,
      {
        params: {
          include_is_storage: includeStorage,
          include_discarded: includeDiscarded,
          offset,
          limit,
        },
      }
    )
    return response.data
  }

  const getDeliveryById = async (deliveryId: string) => {
    try {
      const response = await Axios.get<ItemResponseType<Delivery>>(
        `/deliveries/${deliveryId}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getThinDeliveryById = async (deliveryId: string) => {
    try {
      const response = await Axios.get<ItemResponseType<ThinDelivery>>(
        `/deliveries/thin/${deliveryId}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const setConsistency = async (deliveryId: string, wheatCodeId: string) => {
    try {
      const response = await Axios.get<ItemResponseType<Delivery>>(
        `/consistency/${deliveryId}/${wheatCodeId}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const setDeliveryAsDiscarded = async (deliveryId: number) => {
    try {
      const response = await Axios.put<{
        status: boolean
      }>(`/deliveries/${deliveryId}/discard`)
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const setGroupDeliveries = async (deliveryIds: number[]) => {
    try {
      const response = await Axios.post<{
        status: boolean
        detail: string
      }>(`/deliveries/grouping`, {
        delivery_ids: deliveryIds,
      })
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const updateGroupDeliveries = async (
    deliveryIds: number[],
    groupId: number
  ) => {
    try {
      const response = await Axios.put<{
        status: boolean
        detail: string
      }>(`/deliveries/grouping/${groupId}`, {
        delivery_ids: deliveryIds,
      })
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getSpectralDistanceData = async (deliveryId: number | string) => {
    try {
      const response = await Axios.post<
        ItemResponseType<TSpectralDistanceData>
      >(`/deliveries/spectral_distance/${deliveryId}`)
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const recalculateDeliveriesAccordingToNewTargetBin = async (
    wheatCode: number
  ) => {
    try {
      const response = await Axios.post<IRecalculateBin>(
        `/deliveries/recalculate/target_bin/${wheatCode}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const checkDeliveriesRecalculationStatus = async (taskId: string) => {
    try {
      const response = await Axios.get<IRecalculateBinStatus>(
        `/deliveries/recalculate/status/${taskId}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  return {
    getDeliveries,
    createDelivery,
    updateIncomingDeliveryAttribute,
    updateDelivery,
    getStoredDeliveries,
    getStoredDeliveriesThin,
    getDeliveryById,
    getThinDeliveryById,
    setConsistency,
    setDeliveryAsDiscarded,
    setGroupDeliveries,
    updateGroupDeliveries,
    getSpectralDistanceData,
    recalculateDeliveriesAccordingToNewTargetBin,
    checkDeliveriesRecalculationStatus,
  }
}

export default useDeliveriesService
